/**
 * Normalizzazione degli errori del BFF Limulo (`app-qlash-api`) in UN verdetto.
 *
 * ⚠️ IL FATTO CHE GIUSTIFICA QUESTO FILE: il BFF non ha un contratto d'errore, ne ha
 * QUATTRO, perche' una risposta puo' nascere in tre punti diversi della catena — il
 * middleware d'auth dell'host, il gate app/organizzazione dell'host, il router del BFF —
 * e nessuno dei tre conosce l'envelope degli altri. Misurati in vivo su
 * `staging-api.qlash.gg` il 2026-08-30 (i primi tre) e letti su `qlash-limulo@cabc619`:
 *
 *   host auth   401 → {"error":"No token provided"}                    ne' `ok` ne' `code`
 *   host org    400 → {"code":"E_ORGANIZATION_REQUIRED","error":…}     `code` ma non `ok`
 *   host gate   403 → {"error":"Required app is not active…",
 *                      "requiredApps":[…],"enabledApps":[…]}           `appAccess.ts:802`
 *   BFF        4xx → {"ok":false,"error":…,"code":"E_QLASH_*",
 *                      "correlationId":…}                              `public-errors.js`
 *
 * Chi discrimina su `code` — la cosa che verrebbe naturale — indovina in un caso su
 * quattro. Da qui l'unico punto del client che sa leggere tutte e quattro le forme.
 *
 * ⚠️ E IL LIMITE, dichiarato invece che nascosto: `sanitizePublicDetails`
 * (`public-errors.js:27-35`) ha una whitelist e butta via ogni altro dettaglio prima che
 * esca. Quindi un 403 nato DENTRO il BFF arriva al client NUDO, e resta `forbidden`:
 * `forbidden` non degrada in «non ancora servito da Limulo», perche' dire all'utente una
 * cosa che non sappiamo e' peggio che dirgli che non lo sappiamo.
 *
 * 📌 **Questa riga diceva «whitelist di CINQUE chiavi» e «`settingsKey` buttato via due
 * volte». Rimisurato il 2026-09-06 su `qlash-limulo@origin/staging` = `46404fc`: le chiavi
 * sono SETTE** — `allowedMimeTypes`, `maxBytes`, `mimeType`, `profileId`, `size`, e in piu'
 * **`endpointId`** e **`settingsKey`**. Cioe' proprio le due che le note qui sotto davano per
 * perdute. Cosa NON cambia: `assertEndpointEnabled` costruisce l'errore **senza**
 * `isPublicError`, quindi `sendPublicError` forza comunque 500 e `details: undefined` — la
 * whitelist non ce la fa nemmeno a vederlo. Cioe' il verdetto resta giusto e la MOTIVAZIONE
 * era doppia e adesso e' singola. Il valore va riletto alla fonte prima di contarci: e' un
 * file di un altro repo, cambia senza lasciare qui nessun segnale.
 */

export type LimuloVerdict =
  /** 401 dell'host: token assente, malformato o scaduto. Si rifa' l'auth. */
  | 'unauthenticated'
  /**
   * 🔴 **NOI abbiamo rinunciato ad aspettare.** Non e' un rifiuto di nessuno: la richiesta
   * e' stata abbandonata dalla nostra scadenza (`fetchWithTimeout`), e il token era valido.
   *
   * Aggiunto il 2026-09-06 dopo un difetto che avevamo introdotto UN'ORA prima con #178:
   * la scadenza sulla catena di sessione mordeva (45 s → 18 s, bene), ma l'utente leggeva
   * «Identita' non valida — Serve una nuova autenticazione». Cioe' gli dicevamo di **rifare
   * il login** — dentro Telegram, col ponte verso il browser di sistema — quando gli
   * bastava **riprovare**.
   * 📌 *E' il costo piu' alto che una copy sbagliata possa avere in questa app, ed era
   * nostro: le altre bugie di stasera le abbiamo trovate, questa l'abbiamo fatta.*
   *
   * ⛔ Non si riusa `network`, e la ragione e' nella frase: `degraded.network` dice
   * «Controlla la connessione», che e' vero per una rete caduta e **discutibile per una
   * scadenza che abbiamo scelto noi a 15 s** — la connessione dell'utente puo' essere
   * perfetta. La differenza da dire e' «ci ho rinunciato io», non «non sei tu».
   */
  | 'timeout'
  /** 403 dell'host: l'app `qlash-api` non e' attiva sull'organizzazione. */
  | 'app-off'
  /** 403 dell'host: la capability richiesta non e' abilitata sull'organizzazione. */
  | 'capability-off'
  /** 403 dell'host: l'organizzazione selezionata non combacia col membership del token. */
  | 'org-mismatch'
  /** 400 dell'host: manca del tutto un'organizzazione autenticata. */
  | 'org-required'
  /**
   * 424 del BFF. ⚠️ NON significa solo «nessun provider»: `router.js:210-232` fa uscire
   * con lo stesso status e lo stesso messaggio anche il torneo INESISTENTE e il filtro
   * invalido. Il motivo vero resta in `internalDetails`, che al client non arriva mai.
   * Il nome del verdetto dice quel dubbio invece di nasconderlo.
   */
  | 'domain-or-missing'
  /** 403 che non porta indizi: NON si indovina quale dei tre significati sia. */
  | 'forbidden'
  /** 401 del BFF: il token ESTERNO e' stato rifiutato. Nessun refresh puo' curarlo. */
  | 'external-token-invalid'
  /** 502 del BFF: l'issuer OIDC non risponde. Non e' un guasto nostro. */
  | 'identity-provider-down'
  /** 403 del BFF: l'issuer non dichiara verificata l'email dell'utente. */
  | 'email-not-verified'
  /** 500 con `E_QLASH_INTERNAL`: guasto vero **oppure** interruttore d'org spento. */
  | 'internal-or-switch-off'
  | 'bad-request'
  | 'not-found'
  /**
   * 409. ⚠️ **Oggi non puo' arrivare, ed e' un ramo di CONTRATTO, non un'osservazione.**
   * Il dominio del provider dichiara `http: 409` su «qualcuno ha gia' refertato un punteggio
   * diverso» (`qTournaments core/risultati.ts:191-198`), «check-in su incontro concluso»
   * (`:216-217`) e «prove senza referto» (`:222-223`) — ma il runtime dell'host appiattisce
   * ogni rifiuto del provider in una stringa e risponde 424 (v. `matchActions.ts`, cappello).
   * Il ramo resta perche' il giorno in cui l'host smette di appiattire, un 409 che cade in
   * `unknown` direbbe «esito non riconosciuto» su un messaggio perfettamente chiaro.
   */
  | 'conflict'
  /** 413: file oltre il limite. Il BFF lo lancia DA SOLO, con `maxBytes`/`size` nei dettagli. */
  | 'payload-too-large'
  /** 415: tipo file non ammesso. Stessa origine del 413, con `mimeType`/`allowedMimeTypes`. */
  | 'unsupported-media'
  | 'server'
  /** La richiesta non e' mai arrivata: rete caduta o timeout. */
  | 'network'
  | 'unknown';

export interface LimuloFailure {
  verdict: LimuloVerdict;
  /** Lo status HTTP quando c'e' stata una risposta; `null` se la rete non e' arrivata. */
  status: number | null;
  /** `E_QLASH_*` o `E_ORGANIZATION_REQUIRED`; `null` per gli envelope dell'host che non ne hanno. */
  code: string | null;
  /** Il messaggio del server, mai inventato: stringa vuota se non ne ha mandato uno. */
  message: string;
  /** L'unica cosa che permette a Fabio di ritrovare la richiesta nei log del BFF. */
  correlationId: string | null;
  /** Popolato solo dal 403 dell'host: quali app mancano. Mai `undefined`. */
  requiredApps: string[];
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/**
 * Il 403 «organizzazione che non combacia» e' l'unico dei tre che non porta un campo
 * strutturato: si riconosce solo dal messaggio (`organization-context.ts:66-70`). E' un
 * riconoscimento fragile per costruzione — se il messaggio cambia lato server, il caso
 * ricade su `forbidden`, che e' il fallback giusto: si perde precisione, non correttezza.
 */
const ORG_MISMATCH_MARKER = 'does not match the active membership';
/**
 * La QUINTA busta, trovata il 2026-09-05 addosso a un utente vero: il middleware d'auth
 * dell'host (`middlewares/auth.ts:282`) risponde `400 {error: "X-AM-ORGANIZATION header
 * is required"}` — **senza `code` e senza `ok`**, quindi nessuno dei rami basati sul
 * codice la intercettava e finiva in `bad-request`, cioe' nella copy «esito non
 * riconosciuto».
 *
 * Quella schermata ha fatto la cosa giusta — ha riportato il messaggio grezzo invece di
 * inventare — ma un verdetto che esiste gia' (`org-required`) dice la stessa cosa in modo
 * utilizzabile. Si riconosce dal MESSAGGIO perche' non c'e' altro da leggere.
 */
const HOST_ORG_REQUIRED_MARKER = 'X-AM-ORGANIZATION header is required';

/**
 * I tre 403 che il BFF genera DA SOLO, dentro l'handler d'auth
 * (`mobile-auth.js:534-557`, chiamata alla riga 822 di `exchangeMobileToken`): il gate
 * dell'app e' riscritto a mano li' dentro invece che montato come middleware, quindi
 * l'envelope e' quello del BFF e `requiredApps` NON c'e' — `sanitizePublicDetails` lo
 * avrebbe comunque buttato via. Resta il testo del messaggio, in italiano.
 *
 * Riconoscere un errore per stringa e' fragile, e per questo il fallback e' `forbidden`:
 * se il messaggio cambia lato server si perde precisione, non correttezza. Nessuno di
 * questi marcatori inventa un verdetto piu' preciso di quello che il server ha detto.
 */
const BFF_APP_OFF_MARKER = 'non attiva per il tenant';
const BFF_CAPABILITY_OFF_MARKER = 'Capability Qlash API non disponibile';

/**
 * Tre errori dell'auth che il BFF marca con un `code` esplicito
 * (`public-errors.js:5-11`, lanciati in `mobile-auth.js:172/179/323`). Si leggono dal
 * codice, non dal messaggio: dove il codice c'e', usarlo e' obbligatorio — leggere una
 * stringa in italiano avendo a disposizione un codice sarebbe una scelta, non una
 * necessita' come per i 403 del gate app.
 *
 * Meritano un verdetto proprio perche' portano ad azioni diverse: un token esterno
 * rifiutato non si cura rinfrescando (il materiale in ingresso e' sbagliato: issuer,
 * audience, algoritmo o client_id), un issuer giu' non e' colpa nostra, e un'email non
 * verificata e' una cosa che l'utente puo' sistemare.
 */
const VERDICT_BY_CODE: Record<string, LimuloVerdict> = {
  E_QLASH_EXTERNAL_TOKEN_INVALID: 'external-token-invalid',
  E_QLASH_IDENTITY_PROVIDER_UNAVAILABLE: 'identity-provider-down',
  E_QLASH_EMAIL_NOT_VERIFIED: 'email-not-verified',
  E_QLASH_PROVIDER_UNAVAILABLE: 'domain-or-missing',
};

/**
 * ⚠️ UN 500 CON `E_QLASH_INTERNAL` NON E' LA PROVA DI UN BUG DEL SERVER.
 *
 * `assertEndpointEnabled` (`policy.js:97-111`) costruisce un `Error` con
 * `statusCode = 403` ma **senza `isPublicError`**; `sendPublicError` onora lo status
 * solo con quel flag, altrimenti forza 500 con `code: E_QLASH_INTERNAL` e
 * `details: undefined`. Quindi un interruttore d'organizzazione spento — che e'
 * configurazione, non guasto — arriva al client travestito da errore interno, e il
 * campo che direbbe QUALE interruttore (`error.payload.settingsKey`) non esce comunque,
 * perche' l'errore non e' «pubblico» e `sendPublicError` serializza i dettagli solo per
 * quelli. (Fino al 2026-09-06 questa nota aggiungeva «e comunque la whitelist a cinque
 * chiavi non lo conterrebbe»: falso da qualche parte prima di `46404fc`, dove
 * `PUBLIC_DETAIL_KEYS` contiene `settingsKey` ed `endpointId`. La causa che resta e' una
 * sola, ed e' quella che basta.)
 *
 * Il verdetto tiene aperte entrambe le letture. Un 500 senza quel codice resta
 * `server`: li' non abbiamo nessun motivo di dubitare che sia un guasto.
 * (Riportato a Fabio su kb#91; fix minimo: `createPublicHttpError` + un codice dedicato.)
 */

const verdictFor = (
  status: number,
  body: Record<string, unknown>,
  message: string,
  code: string | null,
  requiredApps: string[],
): LimuloVerdict => {
  if (code && VERDICT_BY_CODE[code]) return VERDICT_BY_CODE[code];
  if (status === 401) return 'unauthenticated';
  if (status === 403) {
    if (requiredApps.length > 0) return 'app-off';
    if (asStringArray(body.requiredCapabilities).length > 0) return 'capability-off';
    if (message.includes(BFF_CAPABILITY_OFF_MARKER)) return 'capability-off';
    if (message.includes(BFF_APP_OFF_MARKER)) return 'app-off';
    if (message.includes(ORG_MISMATCH_MARKER)) return 'org-mismatch';
    // Fra i 403 nudi finisce anche `enableAuthExchange === false` («Exchange token
    // Qlash API disabilitato per il tenant»): e' un interruttore del tenant, NON l'app
    // spenta. Chiamarlo `app-off` manderebbe qualcuno ad accendere una cosa gia' accesa.
    return 'forbidden';
  }
  if (status === 400) {
    if (code === 'E_ORGANIZATION_REQUIRED') return 'org-required';
    if (message.includes(HOST_ORG_REQUIRED_MARKER)) return 'org-required';
    return 'bad-request';
  }
  if (status === 404) return 'not-found';
  if (status === 409) return 'conflict';
  // ⚠️ 413 e 415 sono l'ECCEZIONE alla regola del cappello: NON nascono nel provider e non
  // passano dall'appiattimento del runtime. Li lancia il BFF stesso, prima di scendere alla
  // capability (`validateUploadedFile`, `router.js:378-398`), con `isPublicError` e dettagli
  // che stanno nella whitelist — quindi arrivano NOMINATI e utilizzabili. Sono, oggi, gli
  // unici due rifiuti di scrittura su cui si puo' dire all'utente cosa cambiare.
  if (status === 413) return 'payload-too-large';
  if (status === 415) return 'unsupported-media';
  if (status === 424) return 'domain-or-missing';
  if (status >= 500) return code === 'E_QLASH_INTERNAL' ? 'internal-or-switch-off' : 'server';
  return 'unknown';
};

/**
 * Traduce un errore di RTK Query (`{status, data}` per una risposta, `{status:'FETCH_ERROR'}`
 * per una richiesta mai arrivata) nel verdetto unico. Non lancia mai: riceve per definizione
 * cio' che la rete ha prodotto, e una schermata non deve esplodere perche' davanti al BFF
 * ha risposto un nginx con dell'HTML.
 */
export function limuloFailure(error: unknown): LimuloFailure {
  const envelope = asRecord(error);
  const rawStatus = envelope.status;
  const body = asRecord(envelope.data);

  const message = asString(body.error);
  const code = asString(body.code) || null;
  const correlationId = asString(body.correlationId) || null;
  const requiredApps = asStringArray(body.requiredApps);

  if (typeof rawStatus !== 'number') {
    return {
      // ⚠️ I due non sono piu' la stessa cosa, e la differenza e' cio' che l'utente puo'
      // farci: `FETCH_ERROR` e' la rete che e' caduta (controlla la connessione),
      // `TIMEOUT_ERROR` siamo NOI che abbiamo smesso di aspettare (riprova).
      verdict:
        rawStatus === 'TIMEOUT_ERROR'
          ? 'timeout'
          : rawStatus === 'FETCH_ERROR'
            ? 'network'
            : 'unknown',
      status: null,
      code,
      message,
      correlationId,
      requiredApps,
    };
  }

  return {
    verdict: verdictFor(rawStatus, body, message, code, requiredApps),
    status: rawStatus,
    code,
    message,
    correlationId,
    requiredApps,
  };
}

/**
 * ⚠️⚠️ NESSUN CONSUMATORE OGGI — E NON E' «MORTA», E' DIVERGENTE. NON RICOLLEGARLA
 * SENZA DECIDERE PRIMA.
 *
 * Nessun file di `src/` chiama questa funzione (controllo del 2026-09-02). Le schermate
 * passano tutte da `degradedCopyKeys` in `gating.ts`, che e' la fonte VIVA — e le due
 * rispondono alla stessa domanda («questi verdetti vanno trattati insieme?») dando
 * risposte ormai DIVERSE:
 *
 *   qui                     → domain-or-missing · app-off · capability-off  (insieme)
 *   degradedCopyKey (viva)  → domain-or-missing da solo;
 *                             app-off · capability-off insieme
 *
 * La divergenza e' nata con la copy corta decisa da Luca il 2026-08-31 e nessuno l'ha
 * vista, perche' il gate e' verde su questa funzione mentre a schermo gira l'altra.
 * ⇒ Ricollegarla in buona fede **ripristinerebbe un comportamento diverso da quello
 * attuale** senza che nulla lo segnali. Se serve di nuovo, si decide prima QUALE delle
 * due raggruppature e' quella giusta, e si tiene una fonte sola.
 *
 * (Da decidere in fase 3, quando la catena viva dira' se la distinzione serve davvero.
 * Vedi anche la nota gemella su `isAuthFailure`.)
 *
 * ── La sua descrizione originale, che resta valida sul proprio terreno ──
 * «Limulo questa cosa oggi non la serve» — la sola domanda che le schermate fanno per
 * decidere se mostrare il degrado onesto invece di una lista vuota.
 *
 * La lista e' corta di proposito. Un 401 NON e' qui dentro: la trappola documentata nella
 * mappa BFF (l'auth gira PRIMA del gate app, `appAccess.ts:769-820`) fa comparire 401 dove
 * il problema vero e' un 403, e mostrare «non ancora servito» a chi ha solo il token scaduto
 * lo manda a cercare il guasto dove non e'.
 */
export function isNotServed(failure: LimuloFailure): boolean {
  return (
    failure.verdict === 'domain-or-missing' ||
    failure.verdict === 'app-off' ||
    failure.verdict === 'capability-off'
  );
}

/**
 * ⚠️ NESSUN CONSUMATORE OGGI (controllo del 2026-09-02). A differenza di `isNotServed`
 * qui NON c'e' divergenza: nessun'altra funzione risponde alla stessa domanda, quindi
 * ricollegarla non cambierebbe un comportamento esistente — semplicemente oggi nessuno
 * la chiede. La distinzione che nomina servira' in fase 3 (rifare l'exchange contro
 * rimandare al login), ed e' li' che si decide se collegarla o toglierla.
 *
 * ── Descrizione ──
 * L'identita' non regge. Due modi molto diversi, entrambi qui dentro perche' entrambi
 * fermano la sezione: la NOSTRA sessione non vale piu' (`unauthenticated` — si rifa'
 * l'exchange), oppure il token ESTERNO e' stato rifiutato (`external-token-invalid` —
 * rifare l'exchange con lo stesso materiale produrrebbe lo stesso 401).
 */
export function isAuthFailure(failure: LimuloFailure): boolean {
  return failure.verdict === 'unauthenticated' || failure.verdict === 'external-token-invalid';
}


/**
 * Un 401 puo' essere causato da NOI, e in quel caso non e' un problema di identita'.
 *
 * 🔴 Il percorso misurato il 2026-09-06 (or45), che nessuna delle due meta' spiegava da sola:
 * l'exchange di sessione scade ⇒ `getLimuloToken()` torna `null` ⇒ `prepareHeaders` non mette
 * l'header (giustamente: un `Bearer ` vuoto sarebbe peggio) ⇒ **il server risponde 401, e ha
 * ragione**: quella richiesta non portava credenziali. Il 401 e' vero; e' la CAUSA a essere
 * un'altra, e a quel punto nessuno la sa piu'.
 *
 * ⇒ Chi ha visto la scadenza deve poterlo dire a chi vede il 401. Questa funzione e' quella
 * congiunzione, ed e' pura: prende lo status e l'ultimo esito noto del tentativo di sessione.
 *
 * ⛔ Vale SOLO per `timeout`. Un 401 dopo un rifiuto vero e' un rifiuto vero, e riscriverlo
 * nasconderebbe l'unico caso in cui «rifai l'accesso» e' il consiglio giusto.
 */
export function verdictFor401AfterSessionAttempt(
  status: number | string | undefined,
  lastSessionFailure: LimuloFailure | null,
  /**
   * 🔴 C'e' una sessione utilizzabile adesso? Terzo parametro aggiunto su una domanda di
   * or35 in revisione, e chiude una finestra per COSTRUZIONE invece che con una scadenza.
   *
   * La finestra: scadenza al tempo T (memoria = `timeout`), poi un 401 VERO — sessione
   * davvero revocata — prima di qualunque successo che azzeri la memoria. Li' diremmo
   * «riprova» a chi deve rifare l'accesso, e riprovare non lo sbloccherebbe mai: un LOOP,
   * cioe' peggio del difetto che stiamo togliendo.
   *
   * ⇒ La chiusura non e' un timer: e' che «non abbiamo mandato l'header» spiega un 401
   * **solo se davvero non abbiamo una sessione**. Con una sessione in mano, l'header e'
   * partito e il 401 e' un rifiuto vero — e va detto come tale, perche' li' «rifai
   * l'accesso» e' il consiglio giusto.
   *
   * ⚠️ Misurato quanto la finestra fosse raggiungibile prima di chiuderla: nel percorso
   * servito no (l'unico che crea una sessione dopo un fallimento e'
   * `startLimuloSessionFromCurrentLogto`, che azzera la memoria), ma `runRefresh`
   * (`session.ts:177`) e la diagnostica dev scrivono una sessione **senza** azzerarla. Una
   * finestra raggiungibile solo da un percorso dev e' comunque una finestra, e costava un
   * parametro.
   */
  hasSession: boolean,
): 'timeout' | null {
  if (status !== 401) return null;
  if (hasSession) return null;
  return lastSessionFailure?.verdict === 'timeout' ? 'timeout' : null;
}
